import { useCallback, useEffect } from 'react';
import { Button, Card, Col, Divider, Form, Input, Row, Tabs } from 'antd';
import { observer } from 'mobx-react-lite';

import { useResource } from 'app/common/api';
import { usePageNotify } from 'app/common/notifications/features/page-notifications';
import { UserRoleList } from 'app/railways/domains/access/features/roles/role-list';
import { useAuthStatusWriter } from 'app/railways/domains/auth/features/auth-status';
import { useUserProfileStore } from 'app/railways/domains/profile/features/user-profile-data';
import { TUser } from 'app/railways/domains/users/core/user';
import {
  getUserRequest,
  updateUserRequest,
} from 'app/railways/domains/users/requests';
import { Loading } from 'app/ui';

import { LogoutButton } from '../../logout/features/logout-button';

const { TabPane } = Tabs;

const ButtonSubmit = observer(() => {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Button type="primary" htmlType="submit">
        Сохранить
      </Button>
    </div>
  );
});

type UpdatedUser = Partial<TUser> & { id: number };

export const CurrentUserProfile = observer(() => {
  const profileStore = useUserProfileStore();
  const authStatusStore = useAuthStatusWriter();
  const notify = usePageNotify();

  const fetchUserResource = useResource<TUser['id'], TUser>({
    request: getUserRequest,
    errorNotify: notify,
    onAuthRequired: () => authStatusStore.denyAccess(),
    onDone: (userProfile) => profileStore.setProfile(userProfile),
  });

  const updateUserResource = useResource<UpdatedUser, TUser>({
    request: updateUserRequest,
    errorNotify: notify,
    onAuthRequired: () => authStatusStore.denyAccess(),
    onDone: (userProfile) => {
      profileStore.setProfile(userProfile);
      notify.addMessage({
        type: 'success',
        message: 'Данные пользователя обновлены',
      });
    },
  });

  const currentUserId = profileStore.data.id;
  const fetchedUser = profileStore.data;

  const isLoading = fetchUserResource.isLoading || updateUserResource.isLoading;

  useEffect(() => {
    fetchUserResource.send(currentUserId);
  }, [currentUserId, fetchUserResource]);

  const handleUpdateUser = useCallback(
    (updatedUser: UpdatedUser) => {
      updateUserResource.send({
        ...fetchedUser,
        ...updatedUser,
        id: currentUserId,
      });
    },
    [currentUserId, fetchedUser, updateUserResource],
  );

  return (
    <>
      <Card size="small" bodyStyle={{ paddingTop: 0 }}>
        <Loading active={isLoading}>
          <Tabs>
            <TabPane forceRender tab="Данные аккаунта" key="1">
              {/* @TODO: Че т не рендерится при первом рендере */}
              <Form
                layout="vertical"
                initialValues={fetchedUser}
                onFinish={handleUpdateUser}
                key={fetchedUser.id}
              >
                {/* <Form.Item name="id" hidden noStyle /> */}
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item
                      name="login"
                      label="Логин"
                      rules={[{ required: true }]}
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>

                  <Col span={6}>
                    <Form.Item name="password" label="Пароль">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider />

                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <LogoutButton />
                  <ButtonSubmit />
                </div>
              </Form>
            </TabPane>

            <TabPane tab="Роли и разрешения" key="2">
              <Form layout="vertical" onFinish={handleUpdateUser}>
                <Form.Item
                  name="roles"
                  rules={[
                    {
                      required: true,
                      message: 'Пожалуйста, выберите необходимые роли',
                    },
                  ]}
                >
                  <UserRoleList selected={fetchedUser.roles} />
                </Form.Item>
                <Divider />
                <ButtonSubmit />
              </Form>
            </TabPane>
          </Tabs>
        </Loading>
      </Card>
    </>
  );
});
